import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { eur } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, RefreshCw, ChevronDown, ChevronRight, PackageCheck, ExternalLink, AlertTriangle, Boxes, FileDown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import OrdiniSenzaCommessa from "@/components/OrdiniSenzaCommessa";
import FunnelButton from "@/components/FunnelButton";
import SyncStatusPanel from "@/components/SyncStatusPanel";

const fmtDate = (d) => { if (!d) return "—"; const s = String(d).slice(0, 10); return s.split("-").reverse().join("/"); };
const eurN = (v) => (v == null ? "—" : eur(v));
const qty = (v) => (v == null ? "—" : Number(v).toLocaleString("it-IT", { maximumFractionDigits: 2 }));

function ProductMini({ title, rows, kind, statoMap }) {
  if (!rows || rows.length === 0) return null;
  const tot = rows.reduce((a, r) => a + (r.prezzo_totale || 0), 0);
  const hasStato = !!statoMap;
  return (
    <div className="mb-3" data-testid={`commessa-prod-${kind}`}>
      <div className="mb-1 flex items-center gap-2">
        <span className="text-[11px] font-bold uppercase tracking-wide text-primary">{title}</span>
        <Badge variant="secondary" className="text-[10px]">{rows.length}</Badge>
        <span className="ml-auto text-[11px] font-semibold">{eur(tot)}</span>
      </div>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-[11px]">
          <thead className="bg-muted/60 text-left">
            <tr>
              <th className="px-2 py-1 font-semibold">Codice</th>
              <th className="px-2 py-1 font-semibold">Nome</th>
              <th className="px-2 py-1 text-right font-semibold">Q.tà</th>
              {hasStato && <th className="px-2 py-1 text-right font-semibold">Consegnata</th>}
              {hasStato && <th className="px-2 py-1 font-semibold">Assegnata / Stato</th>}
              <th className="px-2 py-1 text-right font-semibold">Prezzo unit.</th>
              <th className="px-2 py-1 text-right font-semibold">Totale</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const st = hasStato ? statoMap[(r.codice || "").toLowerCase()] : null;
              return (
              <tr key={i} className="border-t border-border">
                <td className="px-2 py-1 font-mono text-muted-foreground">{r.codice || "—"}</td>
                <td className="px-2 py-1">{r.nome}</td>
                <td className="px-2 py-1 text-right font-mono">{qty(r.quantita)}</td>
                {hasStato && (
                  <td className="px-2 py-1 text-right font-mono" data-testid={`commessa-art-consegnata-${kind}-${i}`}>
                    {st ? (st.consegnata > 0 ? <span className="font-semibold text-emerald-600 dark:text-emerald-400">{qty(st.consegnata)}</span> : "0") : "—"}
                  </td>
                )}
                {hasStato && (
                  <td className="px-2 py-1" data-testid={`commessa-art-assegnata-${kind}-${i}`}>
                    {st && st.assegnata > 0
                      ? <span className="inline-flex items-center gap-1"><span className="font-mono font-semibold text-amber-600 dark:text-amber-400">{qty(st.assegnata)}</span><span className="text-[10px] text-muted-foreground">{st.stato || "assegnata"}</span></span>
                      : (st ? <span className="text-muted-foreground">—</span> : "—")}
                  </td>
                )}
                <td className="px-2 py-1 text-right font-mono">{eurN(r.prezzo_unitario)}</td>
                <td className="px-2 py-1 text-right font-mono font-semibold">{eurN(r.prezzo_totale)}</td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, sub, accent }) {
  return (
    <Card className="p-4" data-testid={`commesse-kpi-${label}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-1 font-heading text-2xl font-bold ${accent || ""}`}>{value}</div>
      {sub && <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>}
    </Card>
  );
}

function CommessaRow({ c, agentView, canEditNota, funnels, funnelTipi, canFunnel, onFunnel }) {
  const [open, setOpen] = useState(false);
  const [nota, setNota] = useState(c.nota || "");
  const [savingNota, setSavingNota] = useState(false);
  const hasDetails = (c.moduli?.length || c.articoli?.length || c.servizi?.length);
  const salvaNota = async () => {
    setSavingNota(true);
    try {
      await api.post("/commesse/nota", { ordine: c.ordine, nota });
      c.nota = nota;
      toast.success("Nota salvata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Salvataggio nota fallito");
    } finally {
      setSavingNota(false);
    }
  };
  return (
    <>
      <tr className="border-b border-border hover:bg-muted/30" data-testid="commessa-row">
        <td className="py-2 pl-2 pr-2">
          {hasDetails ? (
            <button onClick={() => setOpen((o) => !o)} className="rounded p-0.5 hover:bg-muted" data-testid="commessa-expand">
              {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          ) : <span className="inline-block w-4" />}
        </td>
        <td className="py-2 pr-3 font-mono font-semibold">{c.ordine}</td>
        <td className="py-2 pr-3">
          {c.partner_id ? (
            <Link to={`/partners/${c.partner_id}`} className="inline-flex items-center gap-1 text-primary hover:underline">
              {c.cliente || "—"} <ExternalLink className="h-3 w-3" />
            </Link>
          ) : (c.cliente || "—")}
          {nota && <div className="mt-0.5 max-w-[240px] truncate text-[10px] italic text-muted-foreground" title={nota} data-testid="commessa-nota-riepilogo">Nota: {nota}</div>}
        </td>
        <td className="py-2 pr-3"><FunnelButton numero={c.ordine} tipi={funnelTipi} funnel={(funnels || {})[c.ordine]} canEdit={canFunnel} onSaved={onFunnel} /></td>
        <td className="py-2 pr-3 text-xs text-muted-foreground">
          {c.modalita_pagamento || "—"}
          {c.acconto_pct > 0 && <Badge variant="secondary" className="ml-1 text-[10px]">−{c.acconto_pct}% acc.</Badge>}
        </td>
        <td className="py-2 pr-3 text-right font-mono">{eurN(c.importo_con_iva_eur)}</td>
        <td className="py-2 pr-3 text-right font-mono">{c.kwp ? `${qty(c.kwp)} kWp` : "—"}</td>
        <td className="py-2 pr-3 text-center">
          {c.stato_consegna === "parziale"
            ? <Badge className="bg-amber-500 text-white">Parzialmente evasa</Badge>
            : c.stato_consegna === "consegnata"
              ? <Badge className="bg-emerald-600 text-white">Evasa</Badge>
              : <Badge className="bg-sky-600 text-white">Da evadere</Badge>}
          {(() => {
            const d = c.consegna_piena || [c.data_cons_mf, c.data_cons_el].filter(Boolean).sort().reverse()[0];
            return d ? <div className="mt-0.5 font-mono text-[10px] text-muted-foreground">{fmtDate(d)}</div> : null;
          })()}
        </td>
        {!agentView && (
        <td className="py-2 pr-2 text-right">
          {c.esposta
            ? <div className="flex flex-col items-end gap-0.5" title={c.motivo}>
                <span className="font-mono font-bold text-red-600 dark:text-red-400" data-testid="commessa-esposizione-eur">{eurN(c.esposizione_eur)}</span>
                <Badge className="bg-red-600 text-white text-[10px]">Esposta</Badge>
              </div>
            : <Badge variant="secondary">Saldata</Badge>}
        </td>
        )}
      </tr>
      {open && (
        <tr className="bg-muted/20" data-testid="commessa-detail">
          <td colSpan={agentView ? 7 : 8} className="px-4 py-3">
            <div className="mb-3">
              <div className="rounded-md border border-border p-2">
                <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{agentView ? "Ordine" : "Ordine / Esposizione"}</div>
                <div className="text-sm">Data ordine: <b>{fmtDate(c.data_ordine)}</b></div>
                <div className="text-sm">Consegna piena: <b>{c.consegna_piena ? fmtDate(c.consegna_piena) : "—"}</b></div>
                {!agentView && <div className="text-sm">Scadenza saldo: <b>{c.scadenza ? fmtDate(c.scadenza) : "—"}</b></div>}
                {!agentView && c.acconto_pct > 0 && (
                  <div className="text-sm text-emerald-500">Acconto {c.acconto_pct}% già incassato → esposizione netta: <b>{eurN(c.esposizione_eur)}</b></div>
                )}
                {!agentView && <div className="text-xs text-muted-foreground">{c.motivo}</div>}
              </div>
            </div>
            <ProductMini title="Moduli" rows={c.moduli} kind="moduli" statoMap={c.articoli_stato} />
            <ProductMini title="Articoli" rows={c.articoli} kind="articoli" statoMap={c.articoli_stato} />
            <ProductMini title="Servizi" rows={c.servizi} kind="servizi" statoMap={c.articoli_stato} />
            {c.articoli_stato && (
              <p className="mt-1 text-[10px] text-muted-foreground">
                <span className="font-semibold text-emerald-600 dark:text-emerald-400">Consegnata</span> = merce già consegnata ·
                <span className="ml-1 font-semibold text-amber-600 dark:text-amber-400">Assegnata</span> = merce impegnata (disponibile / in approntamento / in arrivo)
              </p>
            )}
            <div className="mt-3 rounded-md border border-primary/30 bg-primary/5 p-2" data-testid="commessa-nota">
              <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Nota (stampata nel PDF)</div>
              <Textarea
                value={nota}
                onChange={(e) => setNota(e.target.value)}
                readOnly={!canEditNota}
                placeholder={canEditNota ? "Aggiungi una nota per questa commessa…" : "Nessuna nota"}
                rows={2}
                className="text-sm"
                data-testid="commessa-nota-input"
              />
              {canEditNota && (
                <div className="mt-2 flex justify-end">
                  <Button size="sm" onClick={salvaNota} disabled={savingNota || nota === (c.nota || "")} data-testid="commessa-nota-save">
                    {savingNota ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
                    Salva nota
                  </Button>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function Commesse() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | aperte | esposte
  const [syncBusy, setSyncBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const scaricaPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await api.get("/commesse/pdf", { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "report-commesse-aperte.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Errore nella generazione del PDF");
    }
    setPdfBusy(false);
  };
  const canDecideOrfane = ["admin", "back_office"].includes(user?.role) || user?.role_actual === "back_office";
  const canViewOrfane = ["admin", "back_office", "commercial_manager", "head_of_partner"].includes(user?.role);
  const [funnels, setFunnels] = useState({});
  const [funnelTipi, setFunnelTipi] = useState({});
  const loadFunnels = () => api.get("/commesse/funnel").then((r) => { setFunnels(r.data.funnels || {}); setFunnelTipi(r.data.tipi || {}); }).catch(() => {});

  const load = () => { loadFunnels(); return api.get("/commesse").then((r) => setData(r.data)).catch(() => setData(false)); };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const syncCommesse = async () => {
    setSyncBusy(true);
    try {
      const r = await api.post("/admin/sync-commesse");
      toast.success(`Commesse aperte aggiornate: ${r.data.commesse_aperte ?? 0}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sincronizzazione commesse fallita");
    } finally { setSyncBusy(false); }
  };

  const s = data?.summary;
  let rows = data?.commesse || [];
  const agentView = !!data?.agent_view;
  if (filter === "esposte") rows = rows.filter((r) => r.esposta);
  if (q.trim()) {
    const t = q.trim().toLowerCase();
    rows = rows.filter((r) => (r.ordine || "").toLowerCase().includes(t) || (r.cliente || "").toLowerCase().includes(t));
  }

  return (
    <div className="space-y-6" data-testid="commesse-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight">
            <PackageCheck className="h-6 w-6 text-primary" /> Commesse in corso
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Commesse con articoli ancora da consegnare. Una volta consegnata per intero, la commessa esce da questo elenco.
            {data?.aggiornato_al && <span> · Aggiornato al <b>{new Date(data.aggiornato_al).toLocaleString("it-IT")}</b></span>}
          </p>
          {data?.magazzino_fiscale && (
            <div className="mt-2 inline-flex flex-wrap items-center gap-2 rounded-md border border-primary/30 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary" data-testid="magazzino-fiscale-badge">
              <Boxes className="h-3.5 w-3.5" />
              Magazzino fiscale al {data.magazzino_fiscale.data} · {data.magazzino_fiscale.articoli} articoli
              {data.magazzino_fiscale.loaded_at && <span className="font-normal text-muted-foreground">· caricato il {new Date(data.magazzino_fiscale.loaded_at).toLocaleString("it-IT")}</span>}
            </div>
          )}
          {data?.magazzino_movimenti && (
            <div className="mt-2 ml-0 inline-flex flex-wrap items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400" data-testid="magazzino-movimenti-badge">
              <RefreshCw className="h-3.5 w-3.5" />
              Movimenti sincronizzati il {new Date(data.magazzino_movimenti.synced_at).toLocaleString("it-IT")}
              <span className="font-normal text-muted-foreground">
                · {data.magazzino_movimenti.commesse_totali} commesse (installatori {data.magazzino_movimenti.installatori} · impianti {data.magazzino_movimenti.impianti})
              </span>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canViewOrfane && (
            <Button size="sm" variant="outline" onClick={scaricaPdf} disabled={pdfBusy} data-testid="commesse-pdf-btn">
              {pdfBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDown className="mr-2 h-4 w-4" />}Scarica PDF
            </Button>
          )}
          {isAdmin && (
            <Button size="sm" onClick={syncCommesse} disabled={syncBusy} data-testid="commesse-sync-btn">
              {syncBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}Aggiorna
            </Button>
          )}
        </div>
      </div>

      {canViewOrfane && <SyncStatusPanel />}
      {canViewOrfane && <OrdiniSenzaCommessa canDecide={canDecideOrfane} />}

      {data === null ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</div>
      ) : data === false ? (
        <Card className="p-5"><div className="flex items-center gap-2 text-sm text-destructive"><AlertTriangle className="h-4 w-4" /> Impossibile caricare le commesse.</div></Card>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
            <SummaryCard label="Totale" value={s.totale} sub="commesse in corso" />
            <SummaryCard label="Parziali" value={s.parziali ?? 0} sub="parz. evase" accent="text-amber-500" />
            {!agentView && <SummaryCard label="Valore" value={eurN(s.valore_totale)} sub="IVA incl." />}
            {!agentView && <SummaryCard label="Esposte" value={s.esposte} sub="a credito" accent="text-red-500" />}
            {!agentView
              ? <SummaryCard label="Esposizione" value={eurN(s.esposizione_totale)} accent="text-red-500" />
              : <SummaryCard label="kWp totali" value={qty(s.kwp_totale)} />}
          </div>

          <Card className="p-4">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <Input placeholder="Cerca ordine o cliente…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" data-testid="commesse-search" />
              <div className="flex gap-1">
                {[["all", "Tutte"], ...(!agentView ? [["esposte", "Esposte"]] : [])].map(([k, l]) => (
                  <Button key={k} size="sm" variant={filter === k ? "default" : "outline"} onClick={() => setFilter(k)} data-testid={`commesse-filter-${k}`}>{l}</Button>
                ))}
              </div>
              <span className="ml-auto text-xs text-muted-foreground">{rows.length} risultati</span>
            </div>
            {rows.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground" data-testid="commesse-empty">Nessuna commessa trovata.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                    <tr className="border-b border-border">
                      <th className="py-2 pl-2 pr-2" />
                      <th className="py-2 pr-3 font-medium">Ordine</th>
                      <th className="py-2 pr-3 font-medium">Cliente</th>
                      <th className="py-2 pr-3 font-medium">Funnel</th>
                      <th className="py-2 pr-3 font-medium">Pagamento</th>
                      <th className="py-2 pr-3 text-right font-medium">Importo</th>
                      <th className="py-2 pr-3 text-right font-medium">kWp</th>
                      <th className="py-2 pr-3 text-center font-medium">Consegna</th>
                      {!agentView && <th className="py-2 pr-2 text-right font-medium">Esposizione</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((c) => <CommessaRow key={c.ordine} c={c} agentView={agentView} canEditNota={canViewOrfane} funnels={funnels} funnelTipi={funnelTipi} canFunnel={canDecideOrfane} onFunnel={loadFunnels} />)}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
